import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '@/app/store'
import { apiService } from '@/services/api'
import { cartApi } from '../cart/cartAPI'
import { Table } from '@/types'
import { buildEndpoint } from '@/utils/apiUtils'
import { updateCartDataFromApi as updateRegularCartDataFromApi, loadCartItems } from '../cart/cartItemsSlice'
import { updateCartDataFromApi as updateCsCartDataFromApi } from '../csFlow/csCartSlice'
import { setTableLocally } from '../cart/cartSlice'
import { refreshCsCartItems } from '../csFlow/csCartSlice'

// Flutter-style Table Controller - exact match
interface TablesState {
  // Table Data
  tables: Table[]
  isLoadingTables: boolean
  errorMessage: string
  tablesLoaded: boolean // Track if tables have been loaded

  // Table Price
  tablePrice: number // Table price

  // Selected Table
  selectedTable: number
  showTableSelection: boolean // Always show button when order type is "local"

  // Tab Management
  activeTab: number // 0 = Choose Table, 1 = Free Table, 2 = VIP Tables
}

const initialState: TablesState = {
  tables: [],
  isLoadingTables: false,
  errorMessage: '',
  tablesLoaded: false,
  tablePrice: 0.0,
  selectedTable: 0,
  showTableSelection: true,
  activeTab: 0,
}

// Flutter-style async thunks matching exact controller methods
export const loadTables = createAsyncThunk(
  'tables/loadTables',
  async (_, { getState }) => {
    try {
      const state = getState() as RootState

      // Check CS account type - prevent API calls for tables
      if (isCustomerServiceUser(state)) {
        // For CS users, we don't need to load tables
        return { tables: [], tablePrice: 0.0 }
      }

      // Validate token exists before making request
      const token = apiService.getAuthToken()
      if (!token || !token.trim()) {
        throw new Error('Authentication token is missing. Please login again.')
      }

      // Use buildEndpoint to automatically handle CS vs cashier
      const response = await apiService.axios.get(buildEndpoint('/cashier/tables'))

      if (response.status === 200) {
        const responseData = response.data.data ?? response.data

        // Extract table price
        let tablePrice = 0.0
        try {
          const tablePrices = responseData.tablePrices
          if (tablePrices?.individuals_table_price) {
            tablePrice = parseFloat(tablePrices.individuals_table_price.toString()) || 0.0
          }
        } catch (e) {
          tablePrice = 0.0
        }

        // Data is in data.tables
        const tablesData = responseData.tables ?? responseData

        let tables: Table[] = []
        if (Array.isArray(tablesData)) {
          tables = tablesData.map((json: any) => {
            // Convert state (Used/Free) to status (occupied/available)
            let status: 'available' | 'occupied' | 'reserved' | 'maintenance' = 'available'
            if (json.state === 'Used') {
              status = 'occupied'
            } else if (json.state === 'Free') {
              status = 'available'
            } else if (json.status) {
              status = json.status
            }

            const isVipTable = Number(json.isVIP) === 1
            return {
              ...json,
              status,
              isVIP: isVipTable,
              type: json.type,
            } as Table
          })
        }

        return { tables, tablePrice }
      } else {
        throw new Error('Failed to load tables')
      }
    } catch (error: any) {
      // Better error handling - extract meaningful error message
      if (error?.response?.status === 500) {
        const errorMessage = error?.response?.data?.message || error?.response?.data?.error || 'Server error'
        if (errorMessage.includes('is_active')) {
          throw new Error('Employee account issue: Please contact administrator or try logging in again')
        }
        throw new Error(`Server error: ${errorMessage}`)
      } else if (error?.response?.status === 401) {
        throw new Error('Authentication failed: Please login again')
      } else if (error?.response?.status === 403) {
        throw new Error('Access denied: You do not have permission to view tables')
      } else if (error?.message) {
        throw new Error(error.message)
      }
      throw new Error('Error loading tables: ' + (error?.response?.data?.message || 'Unknown error'))
    }
  }
)

export const refreshTables = createAsyncThunk(
  'tables/refreshTables',
  async (_, { getState }) => {
    try {
      const state = getState() as RootState

      // Check CS account type - prevent API calls for tables
      if (isCustomerServiceUser(state)) {
        // For CS users, we don't need to load tables
        return { tables: [], tablePrice: 0.0 }
      }

      // Validate token exists before making request
      const token = apiService.getAuthToken()
      if (!token || !token.trim()) {
        throw new Error('Authentication token is missing. Please login again.')
      }

      // Use buildEndpoint to automatically handle CS vs cashier
      const response = await apiService.axios.get(buildEndpoint('/cashier/tables'))

      if (response.status === 200) {
        const responseData = response.data.data ?? response.data

        // Extract table price
        let tablePrice = 0.0
        try {
          const tablePrices = responseData.tablePrices
          if (tablePrices?.individuals_table_price) {
            tablePrice = parseFloat(tablePrices.individuals_table_price.toString()) || 0.0
          }
        } catch (e) {
          tablePrice = 0.0
        }

        // Data is in data.tables
        const tablesData = responseData.tables ?? responseData

        let tables: Table[] = []
        if (Array.isArray(tablesData)) {
          tables = tablesData.map((json: any) => {
            // Convert state (Used/Free) to status (occupied/available)
            let status: 'available' | 'occupied' | 'reserved' | 'maintenance' = 'available'
            if (json.state === 'Used') {
              status = 'occupied'
            } else if (json.state === 'Free') {
              status = 'available'
            } else if (json.status) {
              status = json.status
            }

            return {
              ...json,
              status,
            } as Table
          })
        }

        return { tables, tablePrice }
      } else {
        throw new Error('Failed to refresh tables')
      }
    } catch (error: any) {
      // Better error handling - extract meaningful error message
      if (error?.response?.status === 500) {
        const errorMessage = error?.response?.data?.message || error?.response?.data?.error || 'Server error'
        if (errorMessage.includes('is_active')) {
          throw new Error('Employee account issue: Please contact administrator or try logging in again')
        }
        throw new Error(`Server error: ${errorMessage}`)
      } else if (error?.response?.status === 401) {
        throw new Error('Authentication failed: Please login again')
      } else if (error?.response?.status === 403) {
        throw new Error('Access denied: You do not have permission to view tables')
      } else if (error?.message) {
        throw new Error(error.message)
      }
      throw new Error('Error refreshing tables: ' + (error?.response?.data?.message || 'Unknown error'))
    }
  }
)

export const selectTable = createAsyncThunk(
  'tables/selectTable',
  async (tableId: number, { getState, dispatch }) => {
    try {
      const state = getState() as RootState

      // Check table existence
      const table = getTableById(state, tableId)
      if (!table) {
        throw new Error('Table not found')
      }

      // Use new API to apply table to cart
      await applyTableToCart(tableId, state, dispatch)

      return tableId
    } catch (error) {
      throw error
    }
  }
)

export const clearSelectedTable = createAsyncThunk(
  'tables/clearSelectedTable',
  async (_, { getState, dispatch }) => {
    try {
      const state = getState() as RootState

      // Use new API to remove table from cart
      await removeTableFromCart(state, dispatch)

      return true
    } catch (error) {
      throw error
    }
  }
)

export const freeSelectedTable = createAsyncThunk(
  'tables/freeSelectedTable',
  async (_, { getState, dispatch }) => {
    try {
      const state = getState() as RootState

      if (state.tables.selectedTable <= 0) {
        throw new Error('Please select a table to free')
      }

      const tableId = state.tables.selectedTable

      // Make API call to free the table
      const response = await apiService.axios.post(
        buildEndpoint(`/cashier/tables/${tableId}/free`)
      )

      if (response.status === 200) {
        // Refresh tables after freeing
        await dispatch(refreshTables() as any)
        // Clear selected table
        return { tableId, success: true }
      } else {
        throw new Error('Failed to free table')
      }
    } catch (error) {
      throw error
    }
  }
)

// Flutter-style Table Slice - exact match
const tablesSlice = createSlice({
  name: 'tables',
  initialState,
  reducers: {
    // Flutter-style reducers matching exact controller methods
    initializeTables: (state) => {
      // Check CS account type first - prevent table loading
      if (isCustomerServiceUserFromState(state)) {
        // For CS users, we don't need to load tables
        state.tables = []
        state.tablesLoaded = true
        state.isLoadingTables = false
        state.errorMessage = ''
        state.selectedTable = 0
        state.showTableSelection = false // Hide table selection for CS account
        return
      }
    },

    clearTables: (state) => {
      state.tables = []
      state.tablesLoaded = false
      state.errorMessage = ''
      state.selectedTable = 0
      state.isLoadingTables = false
    },

    confirmTableSelection: (state) => {
      if (state.selectedTable > 0) {
        state.showTableSelection = false
      }
    },

    showTableSelectionPopup: (state) => {
      state.showTableSelection = true
    },

    switchTab: (state, action: PayloadAction<number>) => {
      state.activeTab = action.payload
    },

    clearTable: (state) => {
      state.selectedTable = 0
      state.tablePrice = 0.0
      state.showTableSelection = true
    },

    checkAndAutoClearTable: (state) => {
      if (state.selectedTable > 0 && !isTableSelectionAllowed(state)) {
        state.selectedTable = 0
        state.tablePrice = 0.0
      }
    },

    forceClearTableOnCartEmpty: (state) => {
      if (state.selectedTable > 0) {
        state.selectedTable = 0
        state.tablePrice = 0.0
      }
    },

    setLocalSelectedTable: (state, action: PayloadAction<number>) => {
      state.selectedTable = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadTables.pending, (state) => {
        state.isLoadingTables = true
        state.errorMessage = ''
      })
      .addCase(loadTables.fulfilled, (state, action) => {
        state.isLoadingTables = false
        state.tables = action.payload.tables
        state.tablePrice = action.payload.tablePrice
        state.tablesLoaded = true
        state.errorMessage = ''
      })
      .addCase(loadTables.rejected, (state, action) => {
        state.isLoadingTables = false
        state.errorMessage = action.error.message || 'Error loading tables'
      })
      .addCase(refreshTables.pending, (state) => {
        state.isLoadingTables = true
        state.errorMessage = ''
      })
      .addCase(refreshTables.fulfilled, (state, action) => {
        state.isLoadingTables = false
        state.tables = action.payload.tables
        state.tablePrice = action.payload.tablePrice
        state.tablesLoaded = true
        state.errorMessage = ''
      })
      .addCase(refreshTables.rejected, (state, action) => {
        state.isLoadingTables = false
        state.errorMessage = action.error.message || 'Error refreshing tables'
      })
      .addCase(selectTable.fulfilled, (state, action) => {
        state.selectedTable = action.payload
      })
      .addCase(selectTable.rejected, (state) => {
        // In case of error, reset selected table
        state.selectedTable = 0
      })
      .addCase(clearSelectedTable.fulfilled, (state) => {
        state.selectedTable = 0
        state.tablePrice = 0.0
      })
      .addCase(clearSelectedTable.rejected, (state) => {
        // In case of error, reset table locally
        state.selectedTable = 0
        state.tablePrice = 0.0
      })
      .addCase(freeSelectedTable.fulfilled, (state, action) => {
        // Table freed successfully, clear selected table
        if (action.payload?.success) {
          state.selectedTable = 0
        }
      })
      .addCase(freeSelectedTable.rejected, (state) => {
        // Error freeing table - keep selected table for retry
      })
  },
})

// Flutter-style helper functions - exact match
function isCustomerServiceUser(_state: RootState): boolean {
  try {
    const employeeDataStr = localStorage.getItem('employee_data')
    if (!employeeDataStr) return false

    const employeeData = JSON.parse(employeeDataStr)

    // Check user type - improve check
    let userType = ''

    // Check in employee
    if (employeeData.employee) {
      const employee = employeeData.employee
      userType = employee.user_type?.toString() ?? employee.type?.toString() ?? ''
    }

    // Direct check in employeeData
    if (userType.length === 0) {
      userType = employeeData.user_type?.toString() ?? employeeData.type?.toString() ?? ''
    }

    // If user type is CS or customer_service
    const isCS =
      userType.toLowerCase().includes('cs') ||
      userType.toLowerCase().includes('customer_service') ||
      userType.toLowerCase().includes('customer service')

    return isCS
  } catch (e) {
    return false
  }
}

function isCustomerServiceUserFromState(_state: TablesState): boolean {
  try {
    const employeeDataStr = localStorage.getItem('employee_data')
    if (!employeeDataStr) return false

    const employeeData = JSON.parse(employeeDataStr)

    // Check user type - improve check
    let userType = ''

    // Check in employee
    if (employeeData.employee) {
      const employee = employeeData.employee
      userType = employee.user_type?.toString() ?? employee.type?.toString() ?? ''
    }

    // Direct check in employeeData
    if (userType.length === 0) {
      userType = employeeData.user_type?.toString() ?? employeeData.type?.toString() ?? ''
    }

    // If user type is CS or customer_service
    const isCS =
      userType.toLowerCase().includes('cs') ||
      userType.toLowerCase().includes('customer_service') ||
      userType.toLowerCase().includes('customer service')

    return isCS
  } catch (e) {
    return false
  }
}

function getTableById(state: RootState, id: number): Table | null {
  try {
    return state.tables.tables.find((table) => table.id === id) || null
  } catch (e) {
    return null
  }
}

function isTableSelectionAllowed(state: TablesState): boolean {
  // Prevent showing table selection for CS account
  if (isCustomerServiceUserFromState(state)) {
    return false
  }

  try {
    // Check if order type allows table selection
    // Note: In a real implementation, you'd check the order types state
    return true
  } catch (e) {
    return false
  }
}

async function applyTableToCart(tableId: number, state: RootState, dispatch: any): Promise<void> {
  try {
    // Get customer or cashier ID
    let userId: number | undefined
    try {
      if (state.customer?.hasCustomerData && state.customer?.customerId) {
        userId = state.customer.customerId
      }
    } catch (e) {
      // Ignore errors in getting customer ID
    }

    // If no customer, use cashier ID
    if (!userId) {
      try {
        const employeeDataStr = localStorage.getItem('employee_data')
        if (employeeDataStr) {
          const employeeData = JSON.parse(employeeDataStr)
          userId = employeeData.user?.id || 7
        }
      } catch (e) {
        userId = 7
      }
    }

    // Get coupon code if available
    let couponCode: string | undefined
    try {
      if (state.discount?.hasDiscount && state.discount?.discountCode?.length > 0) {
        couponCode = state.discount.discountCode
      }
    } catch (e) {
      // Ignore errors in getting coupon code
    }

    // Use new API to apply table
    const response = await dispatch(cartApi.endpoints.applyTable.initiate({
      userId: userId!,
      tableId,
      couponCode,
    }))

    if (response.data?.data) {
      const data = response.data.data

      // Update table price from API
      if (data.table_price) {
        dispatch(setTablePrice(parseFloat(data.table_price.toString()) || 0.0))
      }

      // Save table_id to cartSlice
      dispatch(setTableLocally(tableId))

      // Ensure table_id is in the data before updating cartInfo
      if (!data.table_id) {
        data.table_id = tableId.toString()
      }

      // Update cart data using new data from API
      await updateCartDataFromApi(data, dispatch)

      // Reload cart items from API to ensure all data is up to date
      // Check if user is CS or regular
      const employeeDataStr = localStorage.getItem('employee_data')
      let employeeType = 'cashier'
      if (employeeDataStr) {
        try {
          const employeeData = JSON.parse(employeeDataStr)
          employeeType = employeeData.user?.type || employeeData.user_type || 'cashier'
        } catch (e) {
          // Ignore error
        }
      }

      if (employeeType === 'customer_service' || employeeType?.toString().toLowerCase().includes('cs')) {
        // For CS users, refresh CS cart
        if (userId) {
          await dispatch(refreshCsCartItems({ customerId: userId, forceRefresh: true }) as any)
        }
      } else {
        // For regular users, reload cart items with tableId
        await dispatch(loadCartItems({
          forceRefresh: true,
          tableId: tableId,
          couponCode: couponCode
        }) as any)
      }
    }
  } catch (e) {
    // Ignore errors in applying table
  }
}

async function removeTableFromCart(state: RootState, dispatch: any): Promise<void> {
  try {
    // Get customer or cashier ID
    let userId: number | undefined
    try {
      if (state.customer?.hasCustomerData && state.customer?.customerId) {
        userId = state.customer.customerId
      }
    } catch (e) {
      // Ignore errors in getting customer ID
    }

    // If no customer, use cashier ID
    if (!userId) {
      try {
        const employeeDataStr = localStorage.getItem('employee_data')
        if (employeeDataStr) {
          const employeeData = JSON.parse(employeeDataStr)
          userId = employeeData.user?.id || 7
        }
      } catch (e) {
        userId = 7
      }
    }

    // Get coupon code if available
    let couponCode: string | undefined
    try {
      if (state.discount?.hasDiscount && state.discount?.discountCode?.length > 0) {
        couponCode = state.discount.discountCode
      }
    } catch (e) {
      // Ignore errors in getting coupon code
    }

    // Use new API to remove table
    const response = await dispatch(cartApi.endpoints.removeTable.initiate({
      userId: userId!,
      couponCode,
    }))

    if (response.data?.data) {
      const data = response.data.data

      // Clear table_id from cartSlice
      dispatch(setTableLocally(''))

      // Update cart data using new data from API
      await updateCartDataFromApi(data, dispatch)

      // Reload cart items from API to ensure all data is up to date
      // Check if user is CS or regular
      const employeeDataStr = localStorage.getItem('employee_data')
      let employeeType = 'cashier'
      if (employeeDataStr) {
        try {
          const employeeData = JSON.parse(employeeDataStr)
          employeeType = employeeData.user?.type || employeeData.user_type || 'cashier'
        } catch (e) {
          // Ignore error
        }
      }

      if (employeeType === 'customer_service' || employeeType?.toString().toLowerCase().includes('cs')) {
        // For CS users, refresh CS cart
        if (userId) {
          await dispatch(refreshCsCartItems({ customerId: userId, forceRefresh: true }) as any)
        }
      } else {
        // For regular users, reload cart items without tableId
        await dispatch(loadCartItems({
          forceRefresh: true,
          couponCode: couponCode
        }) as any)
      }
    }
  } catch (e) {
    // Ignore errors in removing table
  }
}

async function updateCartDataFromApi(cartData: Record<string, any>, dispatch: any): Promise<void> {
  try {
    // Check user type and update appropriate cart
    const employeeDataStr = localStorage.getItem('employee_data')
    let employeeType = 'cashier'
    if (employeeDataStr) {
      try {
        const employeeData = JSON.parse(employeeDataStr)
        employeeType = employeeData.user?.type || employeeData.user_type || 'cashier'
      } catch (e) {
        // Ignore error
      }
    }

    if (employeeType === 'customer_service' || employeeType?.toString().toLowerCase().includes('cs')) {
      // Update CS Cart Controller
      dispatch(updateCsCartDataFromApi(cartData))
    } else {
      // Update regular Cart Controller
      dispatch(updateRegularCartDataFromApi(cartData))
    }
  } catch (e) {
    // Ignore errors in update
  }
}

// Flutter-style selectors - exact match
export const selectTables = (state: RootState) => state.tables.tables
export const selectIsLoadingTables = (state: RootState) => state.tables.isLoadingTables
export const selectErrorMessage = (state: RootState) => state.tables.errorMessage
export const selectTablesLoaded = (state: RootState) => state.tables.tablesLoaded
export const selectTablePrice = (state: RootState) => state.tables.tablePrice
export const selectSelectedTable = (state: RootState) => state.tables.selectedTable
export const selectShowTableSelection = (state: RootState) => state.tables.showTableSelection
export const selectActiveTab = (state: RootState) => state.tables.activeTab

export const selectTableById = (state: RootState, id: number) => {
  return state.tables.tables.find((table) => table.id === id) || null
}

export const selectTableStatusColor = (_state: RootState, table: Table) => {
  switch (table.status) {
    case 'occupied':
      return '#E53E3E' // Red
    case 'reserved':
      return '#3182CE' // Blue
    default:
      return '#CBD5E0' // Grey for available
  }
}

export const selectTableStatusText = (_state: RootState, table: Table) => {
  switch (table.status) {
    case 'occupied':
      return 'occupied'
    case 'reserved':
      return 'reserved'
    default:
      return 'available'
  }
}

export const selectIsTableAvailable = (_state: RootState, table: Table) => {
  return table.status === 'available' || !table.status
}

export const selectIsTableSelected = (state: RootState, table: Table) => {
  return state.tables.selectedTable === table.id
}

export const selectShouldShowTableSelection = (state: RootState) => {
  // Prevent showing table selection for CS account
  if (isCustomerServiceUser(state)) {
    return false
  }

  try {
    // Check if order type allows table selection
    // Note: In a real implementation, you'd check the order types state
    return true
  } catch (e) {
    return false
  }
}

export const selectShouldApplyTablePrice = (state: RootState) => {
  try {
    // Check if order type is dine-in
    // Note: In a real implementation, you'd check the order types state
    const isDineIn = true // Placeholder
    const hasSelectedTable = state.tables.selectedTable > 0

    // Check table status - table price is not added if table is occupied (Used)
    let isTableFree = true
    if (hasSelectedTable) {
      const selectedTableModel = selectTableById(state, state.tables.selectedTable)
      if (selectedTableModel) {
        // If table is occupied (busy) or pending, table price is not added
        isTableFree = selectedTableModel.status === 'available'
      }
    }

    const shouldApply = isDineIn && hasSelectedTable && isTableFree

    return shouldApply
  } catch (e) {
    return false
  }
}

export const {
  initializeTables,
  clearTables,
  confirmTableSelection,
  showTableSelectionPopup,
  switchTab,
  clearTable,
  checkAndAutoClearTable,
  forceClearTableOnCartEmpty,
  setLocalSelectedTable,
} = tablesSlice.actions

// Additional action for setting table price
export const setTablePrice = (price: number) => ({
  type: 'tables/setTablePrice',
  payload: price,
})

export default tablesSlice.reducer